import { useLayoutEffect, useEffect, useRef, useCallback } from "react";
import { LogoIcon } from "../../SVGS";
import { Paragraph } from "../typography";
import { AuthLayoutStyles } from "./authLayout.styles";

const AuthLayout = ({ children, contentKey }) => {
  const logoRef = useRef();
  const contentRef = useRef();
  const prevLogoTop = useRef(null);
  const isFirstRender = useRef(true);
  const isAnimating = useRef(false);

  const replayContentAnimation = useCallback(() => {
    if (!contentRef.current) return;
    contentRef.current.style.animation = "none";
    // Force reflow
    void contentRef.current.offsetHeight;
    contentRef.current.style.animation = "";
  }, []);

  useLayoutEffect(() => {
    if (!logoRef.current) return;

    const currentTop = logoRef.current.getBoundingClientRect().top;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      // First render: drop-in animation
      isAnimating.current = true;
      logoRef.current.style.opacity = "0";
      logoRef.current.style.transform = "translateY(-12px)";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (logoRef.current) {
            logoRef.current.style.transition =
              "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
            logoRef.current.style.opacity = "1";
            logoRef.current.style.transform = "translateY(0)";
          }
          setTimeout(() => { isAnimating.current = false; }, 350);
        });
      });
    } else if (
      prevLogoTop.current !== null &&
      Math.abs(prevLogoTop.current - currentTop) > 1
    ) {
      // Subsequent renders: FLIP animation
      isAnimating.current = true;
      const delta = prevLogoTop.current - currentTop;
      logoRef.current.style.transition = "none";
      logoRef.current.style.transform = `translateY(${delta}px)`;

      // Hide content during logo transition
      if (contentRef.current) {
        contentRef.current.style.animation = "none";
        contentRef.current.style.opacity = "0";
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (logoRef.current) {
            logoRef.current.style.transition =
              "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)";
            logoRef.current.style.transform = "translateY(0)";
          }

          // Replay content fadeIn after logo starts moving
          setTimeout(() => {
            replayContentAnimation();
          }, 50);

          setTimeout(() => { isAnimating.current = false; }, 400);
        });
      });
    }

    prevLogoTop.current = currentTop;
  });

  // Detect content size changes from child re-renders (e.g. register step transitions)
  useEffect(() => {
    if (!contentRef.current) return;

    let skipFirst = true;

    const observer = new ResizeObserver(() => {
      if (skipFirst) {
        skipFirst = false;
        return;
      }

      if (isAnimating.current) return;
      if (!logoRef.current || !contentRef.current) return;

      const currentTop = logoRef.current.getBoundingClientRect().top;
      const logoMoved =
        prevLogoTop.current !== null &&
        Math.abs(prevLogoTop.current - currentTop) > 1;

      if (logoMoved) {
        const delta = prevLogoTop.current - currentTop;
        logoRef.current.style.transition = "none";
        logoRef.current.style.transform = `translateY(${delta}px)`;
      }

      contentRef.current.style.animation = "none";
      contentRef.current.style.opacity = "0";

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (logoMoved && logoRef.current) {
            logoRef.current.style.transition =
              "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)";
            logoRef.current.style.transform = "translateY(0)";
          }

          setTimeout(() => {
            replayContentAnimation();
          }, logoMoved ? 50 : 0);
        });
      });

      prevLogoTop.current = currentTop;
    });

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [contentKey, replayContentAnimation]);

  return (
    <AuthLayoutStyles>
      <div className="formContainer">
        <div className="logoWrapper" ref={logoRef}>
          <LogoIcon style={{ color: "#000", width: 48, height: 48 }} />
        </div>
        <div className="contentWrapper" ref={contentRef} key={contentKey}>
          {children}
        </div>
        <Paragraph className="termsText">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </Paragraph>
      </div>
      <div className="imageContainer">
        <img src="/images/login-bg.jpg" alt="Truck" className="loginImage" />
      </div>
    </AuthLayoutStyles>
  );
};

export default AuthLayout;
